import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import ApiKeyInput from './components/ApiKeyInput';
import TradingForm from './components/TradingForm';
import PositionsTable from './components/PositionsTable';
import tradingService, { TRADING_SYMBOL, QuoteData, Position } from './services/MetaCopierService';

// LocalStorage keys
const STORAGE_KEYS = {
  API_KEY: 'tradingapp_api_key',
  ACCOUNT_ID: 'tradingapp_account_id'
};

function App() {
  const [isConnected, setIsConnected] = useState(false);
  const [notifications, setNotifications] = useState<Array<{ id: number; message: string; type: 'success' | 'error' }>>([]);
  const [notificationId, setNotificationId] = useState(0);
  const [quote, setQuote] = useState<QuoteData | null>(null);
  const [positions, setPositions] = useState<Position[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [savedApiKey, setSavedApiKey] = useState<string>('');
  const [savedAccountId, setSavedAccountId] = useState<string>('');
  const hasAutoConnected = useRef(false);
  const quoteInterval = useRef<NodeJS.Timeout | null>(null);
  const positionsInterval = useRef<NodeJS.Timeout | null>(null);

  const showNotification = (message: string, type: 'success' | 'error') => {
    const id = notificationId + 1;
    setNotificationId(id);
    setNotifications(prev => [...prev, { id, message, type }]);

    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 5000);
  };

  const handleConnect = (apiKey: string, accountId: string, silent: boolean = false) => {
    const connected = tradingService.connect(apiKey, accountId);
    setIsConnected(connected);
    
    // Save to localStorage on successful connection
    if (connected) {
      localStorage.setItem(STORAGE_KEYS.API_KEY, apiKey);
      localStorage.setItem(STORAGE_KEYS.ACCOUNT_ID, accountId);
      setSavedApiKey(apiKey);
      setSavedAccountId(accountId);
    }
    
    if (!silent) {
      if (connected) {
        showNotification('Successfully connected to Hydra Trade', 'success');
      } else {
        showNotification('Failed to connect. Please check your credentials', 'error');
      }
    }
  };

  // Auto-connect from URL parameters or localStorage
  useEffect(() => {
    if (hasAutoConnected.current) return;
    
    const urlParams = new URLSearchParams(window.location.search);
    // Support both short (a=accountId, b=apiKey) and long parameter names
    let apiKey = urlParams.get('b') || urlParams.get('apiKey');
    let accountId = urlParams.get('a') || urlParams.get('accountId');
    let source = '';

    // Priority: URL params > localStorage
    if (apiKey && accountId) {
      source = 'URL parameters';
    } else {
      // Try to load from localStorage
      apiKey = localStorage.getItem(STORAGE_KEYS.API_KEY);
      accountId = localStorage.getItem(STORAGE_KEYS.ACCOUNT_ID);
      if (apiKey && accountId) {
        source = 'saved credentials';
        setSavedApiKey(apiKey);
        setSavedAccountId(accountId);
      }
    }

    if (apiKey && accountId) {
      hasAutoConnected.current = true;
      const connected = tradingService.connect(apiKey, accountId);
      setIsConnected(connected);
      
      // Save to localStorage if coming from URL
      if (connected && source === 'URL parameters') {
        localStorage.setItem(STORAGE_KEYS.API_KEY, apiKey);
        localStorage.setItem(STORAGE_KEYS.ACCOUNT_ID, accountId);
        setSavedApiKey(apiKey);
        setSavedAccountId(accountId);
      }
      
      if (connected) {
        showNotification(`Successfully connected using ${source}`, 'success');
      } else {
        showNotification('Failed to connect. Please check your credentials', 'error');
      }
    } else {
      // Load saved credentials into form (but don't auto-connect)
      const savedKey = localStorage.getItem(STORAGE_KEYS.API_KEY);
      const savedId = localStorage.getItem(STORAGE_KEYS.ACCOUNT_ID);
      if (savedKey && savedId) {
        setSavedApiKey(savedKey);
        setSavedAccountId(savedId);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTrade = async (action: 'BUY' | 'SELL', lots: number, tp: number, sl: number) => {
    if (!quote) {
      showNotification('Quote not available. Please wait...', 'error');
      return;
    }

    const result = await tradingService.executeTrade({
      symbol: TRADING_SYMBOL,
      action,
      lots,
      tp,
      sl
    }, quote);

    if (result.success) {
      showNotification(result.message, 'success');
      
      // Fetch positions immediately after trade to get the actual position
      await fetchPositions();
      
      // Check for slippage and adjust TP/SL if needed
      if (result.slippageCheck && (tp > 0 || sl > 0)) {
        setTimeout(async () => {
          const openPositions = await tradingService.getOpenPositions();
          // Find the newly opened position (most recent one for the symbol)
          const newPosition = openPositions
            .filter(p => p.symbol === TRADING_SYMBOL)
            .sort((a, b) => new Date(b.openTime).getTime() - new Date(a.openTime).getTime())[0];
          
          if (newPosition && result.slippageCheck) {
            const expectedPrice = result.slippageCheck.expectedPrice;
            const actualPrice = newPosition.openPrice;
            const priceDiff = Math.abs(actualPrice - expectedPrice);
            
            // If there's slippage (price difference > 0.01)
            if (priceDiff > 0.01) {
              console.log(`Slippage detected: Expected ${expectedPrice}, Got ${actualPrice}`);
              await tradingService.adjustPositionForSlippage(
                newPosition.id,
                actualPrice,
                result.slippageCheck.tpUsd,
                result.slippageCheck.slUsd,
                result.slippageCheck.volume,
                result.slippageCheck.isBuy
              );
              // Refresh positions after adjustment
              fetchPositions();
              showNotification('TP/SL adjusted for slippage', 'success');
            }
          }
        }, 2000); // Wait 2 seconds for position to appear
      }
    } else {
      showNotification(result.message, 'error');
    }
  };

  const fetchPositions = async () => {
    const openPositions = await tradingService.getOpenPositions();
    setPositions(openPositions);
  };

  const handleClosePosition = async (positionId: string) => {
    setIsProcessing(true);
    const result = await tradingService.closePosition(positionId);
    
    if (result.success) {
      showNotification(result.message, 'success');
      // Fetch positions immediately after closing
      fetchPositions();
    } else {
      showNotification(result.message, 'error');
    }
    setIsProcessing(false);
  };

  const removeNotification = (id: number) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const handleDisconnect = () => {
    tradingService.disconnect();
    setIsConnected(false);
    setQuote(null);
    
    // Clear quote fetching interval
    if (quoteInterval.current) {
      clearInterval(quoteInterval.current);
      quoteInterval.current = null;
    }
    
    // Clear positions fetching interval
    if (positionsInterval.current) {
      clearInterval(positionsInterval.current);
      positionsInterval.current = null;
    }
    
    setPositions([]);
    // Note: We keep credentials in localStorage so user can reconnect easily
    showNotification('Disconnected successfully', 'success');
  };

  const handleClearCredentials = () => {
    localStorage.removeItem(STORAGE_KEYS.API_KEY);
    localStorage.removeItem(STORAGE_KEYS.ACCOUNT_ID);
    setSavedApiKey('');
    setSavedAccountId('');
    handleDisconnect();
    showNotification('Credentials cleared from storage', 'success');
  };

  // Fetch quote periodically when connected
  useEffect(() => {
    const fetchQuote = async () => {
      if (isConnected) {
        const quoteData = await tradingService.getQuote(TRADING_SYMBOL);
        if (quoteData) {
          setQuote(quoteData);
        }
      }
    };

    if (isConnected) {
      // Fetch immediately
      fetchQuote();
      
      // Then fetch every 5 seconds
      quoteInterval.current = setInterval(fetchQuote, 5000);
    }

    return () => {
      if (quoteInterval.current) {
        clearInterval(quoteInterval.current);
        quoteInterval.current = null;
      }
    };
  }, [isConnected]);

  // Fetch positions periodically when connected
  useEffect(() => {
    if (isConnected) {
      // Fetch immediately
      fetchPositions();
      
      // Then fetch every 10 seconds
      positionsInterval.current = setInterval(fetchPositions, 10000);
    }

    return () => {
      if (positionsInterval.current) {
        clearInterval(positionsInterval.current);
        positionsInterval.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected]);
  return (
    <div className="App">
      <div className="animated-background">
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
        <div className="gradient-orb orb-3"></div>
      </div>

      <div className="container">
        <main className="app-main">
          {!isConnected ? (
            <ApiKeyInput 
              onConnect={handleConnect} 
              isConnected={isConnected}
              initialApiKey={savedApiKey}
              initialAccountId={savedAccountId}
            />
          ) : (
            <>
              <TradingForm onTrade={handleTrade} isConnected={isConnected} quote={quote} />
              <PositionsTable 
                positions={positions} 
                onClose={handleClosePosition}
                isProcessing={isProcessing}
                quote={quote}
              />
              <div className="disconnect-container">
                <button onClick={handleDisconnect} className="glass-button disconnect-button">
                  <span className="button-text">Disconnect</span>
                  <div className="button-glow"></div>
                </button>
                <button onClick={handleClearCredentials} className="glass-button disconnect-button" style={{ marginLeft: '10px' }}>
                  <span className="button-text">Clear Saved Credentials</span>
                  <div className="button-glow"></div>
                </button>
              </div>
            </>
          )}
        </main>

        <footer className="app-footer">
          <p>© 2026 Trading App</p>
        </footer>
      </div>

      <div className="notifications-container">
        {notifications.map(notification => (
          <div
            key={notification.id}
            className={`notification ${notification.type} notification-enter`}
            onClick={() => removeNotification(notification.id)}
          >
            <span className="notification-icon">
              {notification.type === 'success' ? '✓' : '✕'}
            </span>
            <span className="notification-message">{notification.message}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default App;
